import Vue, { DirectiveBinding, VNode } from 'vue'
import { Validator, createValidator } from './validator'
import { z } from 'zod'
import { parseValidationObject } from './schemas'

// Add validator to Vue prototype
declare module 'vue/types/vue' {
  interface Vue {
    $zodValidator: Validator
  }
}

export type ValidationValue = z.ZodType<unknown> | Record<string, boolean> | null | undefined

interface ValidateElement extends HTMLElement {
  _validateCleanup?: () => void
}

// Options to configure the plugin
interface Options {
  inject?: boolean
  validateOnInput?: boolean
}

// Default options to configure the plugin
export const DefaultOptions: Options = {
  inject: false,
  validateOnInput: false,
}

// Helper methods
const parseSchema = (value: ValidationValue): z.ZodType<unknown> => {
  if (value instanceof z.ZodType) {
    return value
  } else if (value && typeof value === 'object') {
    return parseValidationObject(value)
  } else {
    return z.unknown()
  }
}

/**
 * Sets up event listeners for form validation on an HTML element.
 *
 * This function configures event handlers for input, change, and blur events
 * to properly update and validate field values in the validator instance.
 * It also stores cleanup functions to enable proper removal of event listeners
 * when the component is destroyed.
 *
 * @param el - The HTML element to attach event listeners to
 * @param field - The name of the field being validated
 * @param validator - The validator instance to use for validation
 */
const setupEventListeners = (
  el: ValidateElement,
  field: string,
  validator: Validator,
  vnode: VNode,
): void => {
  // Listen for input/change events
  const onInput = (event: Event) => {
    const value = (event.target as HTMLInputElement)?.value
    validator.setValue(field, value)
    // Force Vue to re-evaluate computed properties that depend on field value
    Vue.nextTick(() => {
      // This empty function forces a reactivity update
    })
  }

  // Blur event to validate field
  const onBlur = () => {
    validator.validateField(field)
    Vue.nextTick(() => {
      // This empty function forces a reactivity update
    })
  }

  const watcherAdded = setupWatcher(field, vnode, validator)

  // If a vue watcher is added there is no need to add event listeners
  if (!watcherAdded) {
    el.addEventListener('input', onInput)
    el.addEventListener('change', onInput)
  }

  el.addEventListener('blur', onBlur)

  // Store listeners for cleanup
  el._validateCleanup = () => {
    if (!watcherAdded) {
      el.removeEventListener('input', onInput)
      el.removeEventListener('change', onInput)
    }
    el.removeEventListener('blur', onBlur)
    validator.removeField(field)
  }
}

/**
 * Sets up a watcher for v-model changes on an element.
 *
 * This function attempts to find a v-model directive on the element and,
 * if found, establishes a Vue watcher on the bound expression. When the
 * value changes in the component data, the watcher will automatically
 * update the field value in the validator instance.
 *
 * @param field - The name of the field being validated
 * @param vnode - The Vue virtual node associated with the element
 * @param validator - The validator instance to use for validation
 */
const setupWatcher = (field: string, vnode: VNode, validator: Validator) => {
  const directives = vnode.data?.directives ?? []
  const vModel = directives.find((directive: DirectiveBinding) => directive.name === 'model')

  if (vModel) {
    vnode.context.$watch(vModel.expression, function (newValue: string) {
      validator.setValue(field, newValue)
    })

    return true
  }

  return false
}

/**
 * Extracts validation options from a Vue virtual node's data attributes.
 *
 * This function searches through the element's attributes for any that start with
 * 'data-vv-' (validation attributes), removes this prefix, and collects them into
 * a Map that can be used for configuring field validation options.
 *
 * @param vnode - The Vue virtual node to extract attributes from
 * @returns A Map of validation option names to their values
 */
const getNodeOpts = (vnode: VNode) => {
  const attrs = vnode.data?.attrs ?? []
  const dataAttrs = new Map()

  Object.entries(attrs).forEach(([key, value]) => {
    if (/data-vv-/.test(key)) {
      const parsedName = key.replace('data-vv-', '')
      dataAttrs.set(parsedName, value)
    }
  })

  return dataAttrs
}

// Create directive
const createValidateDirective = () => {
  return {
    bind(el: HTMLElement, binding: DirectiveBinding, vnode: VNode) {
      const vm = vnode.context
      const validator = vm.$zodValidator
      const field = el.getAttribute('name')

      if (!field) {
        console.error('v-zvalidate directive requires name attribute on element') // eslint-disable-line no-console
        return
      }

      // Parse schema from binding value
      const schema = parseSchema(binding.value)
      const fieldOpts = getNodeOpts(vnode)

      // Register field with validator
      validator.addField(field, schema, el, fieldOpts)

      // Set initial value if exists
      const initialValue = vnode.componentInstance?.$props?.value ?? (el as HTMLInputElement).value
      if (initialValue !== undefined) {
        validator.setValue(field, initialValue)
      }

      // Setup event handlers
      setupEventListeners(el, field, validator, vnode)
    },

    update(el: HTMLElement, binding: DirectiveBinding, vnode: VNode) {
      const vm = vnode.context
      const validator = vm.$zodValidator
      const field = el.getAttribute('name')
      const fieldOpts = getNodeOpts(vnode)

      if (!field) return

      // Only update if the value actually changed
      if (binding.oldValue !== binding.value) {
        // Parse schema from binding value
        const schema = parseSchema(binding.value)

        // Update schema for the field
        validator.addField(field, schema, el, fieldOpts)
      }
    },

    unbind(el: ValidateElement) {
      if (el._validateCleanup) {
        el._validateCleanup()
      }
    },
  }
}

// Main Plugin
export const ZodValidationPlugin = {
  install(VueInstance: typeof Vue, options: Options = DefaultOptions) {
    // Make validator available in all components
    VueInstance.mixin({
      beforeCreate() {
        // Create a new validator instance for each component
        if (!this.$zodValidator) {
          // If inject is disabled, create a new validator
          // Otherwise, try to get from parent
          if (options.inject === true || !this.$parent || !this.$parent.$zodValidator) {
            this.$zodValidator = createValidator()
          } else {
            // Use parent's validator
            this.$zodValidator = this.$parent.$zodValidator
          }
        }
      },
      provide() {
        return {
          $zodValidator: this.$zodValidator,
        }
      },
    })

    // Add directive with a different name to avoid conflicts with vee-validate
    VueInstance.directive('zvalidate', createValidateDirective())
  },
}
